import type { Note, NoteIcon } from '@/types/notes';

const TAG_PATTERN = /(^|\s)#([a-z0-9][\w-]*)/giu;
const RELATIVE_TIME_THRESHOLDS: Array<{ unit: Intl.RelativeTimeFormatUnit; value: number }> = [
  { unit: 'year', value: 60 * 60 * 24 * 365 },
  { unit: 'month', value: 60 * 60 * 24 * 30 },
  { unit: 'week', value: 60 * 60 * 24 * 7 },
  { unit: 'day', value: 60 * 60 * 24 },
  { unit: 'hour', value: 60 * 60 },
  { unit: 'minute', value: 60 },
  { unit: 'second', value: 1 }
];

const relativeTimeFormatter = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });

export const extractNoteTags = (value: string): string[] => {
  const tags = new Set<string>();
  const text = value ?? '';

  for (const match of text.matchAll(TAG_PATTERN)) {
    const rawTag = match[2];
    const normalized = rawTag.trim().toLowerCase();
    if (normalized.length > 0) {
      tags.add(normalized);
    }
  }

  return Array.from(tags).sort((a, b) => a.localeCompare(b));
};

export const detectNoteIconFromText = (value: string): NoteIcon => {
  const text = value ?? '';
  const normalized = text.toLowerCase();

  if (/#done\b/.test(normalized) || normalized.includes('done') || normalized.includes('completed')) {
    return 'done';
  }

  if (normalized.includes('idea') || /#idea\b/.test(normalized) || normalized.includes('brainstorm')) {
    return 'idea';
  }

  if (normalized.includes('research') || /#research\b/.test(normalized) || normalized.includes('source:')) {
    return 'research';
  }

  if (normalized.includes('question') || normalized.includes('?') || /#question\b/.test(normalized)) {
    return 'question';
  }

  if (normalized.includes('urgent') || normalized.includes('!!!') || /#warning\b/.test(normalized)) {
    return 'warning';
  }

  if (normalized.includes('remember') || /#pin\b/.test(normalized)) {
    return 'pin';
  }

  return 'none';
};

export const sortNotesForDisplay = (notes: Note[]): Note[] => {
  return [...notes].sort((a, b) => {
    if (a.isPinned !== b.isPinned) {
      return a.isPinned ? -1 : 1;
    }
    const aUpdated = new Date(a.updatedAt).getTime();
    const bUpdated = new Date(b.updatedAt).getTime();
    if (aUpdated !== bUpdated) {
      return bUpdated - aUpdated;
    }
    return a.text.localeCompare(b.text);
  });
};

export const normalizeNote = (note: Note): Note => {
  return {
    ...note,
    tags: Array.isArray(note.tags)
      ? Array.from(new Set(note.tags.map((tag) => tag.trim().toLowerCase()).filter((tag) => tag.length > 0))).sort(
          (a, b) => a.localeCompare(b)
        )
      : [],
    icon: note.icon ?? 'none',
    isPinned: Boolean(note.isPinned),
    isCompleted: Boolean(note.isCompleted)
  };
};

export const formatRelativeTime = (timestamp: string): string => {
  const time = new Date(timestamp).getTime();
  if (Number.isNaN(time)) {
    return 'just now';
  }

  const now = Date.now();
  let deltaSeconds = Math.round((time - now) / 1000);

  for (const { unit, value } of RELATIVE_TIME_THRESHOLDS) {
    if (Math.abs(deltaSeconds) >= value || unit === 'second') {
      const amount = Math.round(deltaSeconds / value);
      return relativeTimeFormatter.format(amount, unit);
    }
  }

  return 'just now';
};

